import axios from 'axios';

import esriConfig from "@arcgis/core/config";
import Map from "@arcgis/core/Map.js";
import Basemap from "@arcgis/core/Basemap.js"
import MapView from "@arcgis/core/views/MapView.js";

import WebMap from "@arcgis/core/WebMap.js";

import FeatureLayer from "@arcgis/core/layers/FeatureLayer.js";
import TileLayer from "@arcgis/core/layers/TileLayer.js";

import Graphic from "@arcgis/core/Graphic.js";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer.js";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol.js";
import Popup from "@arcgis/core/widgets/Popup.js";
import PopupTemplate from "@arcgis/core/PopupTemplate.js";

class MapArcgis {
    constructor(passedElement) {
        let self = this;

        self.elem = passedElement;
        self.apiKey = process.env.ARCGIS_MAP_API_KEY;

        self.addressForm = self.elem.querySelector('[data-map-address-form]');
        self.addressFormInput = self.elem.querySelector('[data-map-address-form-input]');
        self.language = self.elem.getAttribute('data-language');
        self.sections = self.elem.querySelector('[data-map-sections]');
        self.sectionsItemsContainer = self.elem.querySelector('[data-map-section-items]');
        self.sectionsItems = [];
        self.selectedSectionsItemIndex = null;
        self.mapElem = self.elem.querySelector('[data-map]');
        self.sectionBreakdown = self.elem.querySelector('[data-section-breakdown]');
        self.sectionBreakdownButton = self.elem.querySelector('[data-section-breakdown-button]');
        self.sectionBreakdownItems = self.elem.querySelector('[data-section-breakdown-items]');
        self.sectionBreakdownAccordions = self.elem.querySelector('[data-section-breakdown-accordions]');
        self.sectionBreakdownIsOpen = false;
        self.setMarkers = [];
        self.homeMarker = null;
        self.homeCenter = [-3.158307, 52.774288];
        self.homeZoom = 11;
        self.minZoom = 9;
    }

    init() {
        let self = this;

        self.initMap();

        // Toggle section breakdown dropdown
        self.sectionBreakdownButton.addEventListener('click', evt => {
            if (self.sectionBreakdownIsOpen) {
                self.closeSectionBreakdown();
            } else {
                self.openSectionBreakdown();
            }
        });

        // Open section breakdown on page load?
        if (self.sectionBreakdown.hasAttribute('data-section-breakdown-open')) {
            self.openSectionBreakdown();
        }
    }

    initMap() {
        let self = this;

        esriConfig.apiKey = self.apiKey;

        self.basemap = new WebMap({
            apiKey: self.apiKey,
            portalItem: {
                id: '7ba5fedbafb747ec9d50e61b55ca0844'
            }
        });

        self.center = self.homeCenter;

        self.view = new MapView({
            map: self.basemap,
            container: 'map',
            center: self.center,
            zoom: self.homeZoom,
            constraints: {
                minZoom: self.minZoom
            },
            popup: new Popup({
                dockOptions: {
                    buttonEnabled: false,
                },
                visibleElements: {
                    actionBar: false,
                    collapseButton: false,
                    featureNavigation: false
                }
            })
        });

        self.view.when(() => {
            self.basemap.allLayers.forEach(layer => {
                layer.popupEnabled = false;

                if (layer.allSublayers) {
                    layer.allSublayers.items.forEach(sublayer => {
                        sublayer.popupEnabled = false;
                    });
                }
            });

            self.fetchMapLayers();
            self.fetchMapData();
            self.fetchMapKey();
        });

        self.addressForm.addEventListener('submit', self.getAddressFormSubmission.bind(self));
    }

    openSectionBreakdown() {
        let self = this;

        self.sectionBreakdown.classList.add('is-open');
        self.sectionBreakdownButton.setAttribute('aria-expanded', true);
        self.sectionBreakdownIsOpen = true;
    }

    closeSectionBreakdown() {
        let self = this;

        self.sectionBreakdown.classList.remove('is-open');
        self.sectionBreakdownButton.setAttribute('aria-expanded', false);
        self.sectionBreakdownIsOpen = false;
    }

    fetchMapData() {
        let self = this;

        // Fetch the marker data
        fetch(`/data/map-data-${self.language}.json`)
            .then(function (response) {
                return response.json();
            }).then(function (json) {
                self.mapData = json;

                // Render the sections
                self.renderSections();

                // Listen for click events
                self.events();

                // Load the first section
                self.goToSection(self.mapData[0], self.sectionsItems[0], false);
            }).catch(function (ex) {
                console.log('Parsing failed', ex);
            });
    }

    fetchMapKey() {
        let self = this;

        fetch(`/data/map-key-${self.language}.json`)
            .then(function (response) {
                return response.json();
            }).then(function (json) {
                self.mapKey = json;

                json.map(accordion => {
                    let sectionAccordionContent = '';

                    accordion.items.map(item => {
                        sectionAccordionContent = sectionAccordionContent + `<div class="section-breakdown__item section-breakdown__accordion-panel-item"><span class="section-breakdown__item-icon"><img alt="" src="${item.icon_url}"></span><span>${item.title}</span></div>`;
                    });

                    let collapsed = ' collapsed';
                    let collapse = 'collapse';

                    if (accordion.id == 0) {
                        collapsed = '';
                        collapse = 'collapse show';
                    }

                    self.sectionBreakdownAccordions.insertAdjacentHTML('beforeend', `
                        <li class="section-breakdown__item section-breakdown__accordion-container">
                            <div class="section-breakdown__accordion" data-module="accordion" id="accordion${accordion.id}">
                                <div class="accordion__item">
                                    <div class="accordion__toggle${collapsed}" id="heading${accordion.id}" data-toggle="collapse" data-target="#collapse${accordion.id}" aria-controls="collapse${accordion.id}">
                                        <h4 class="heading accordion__heading">${accordion.title}</h4>
                                    </div>

                                    <div id="collapse${accordion.id}" class="${collapse}" aria-labelledby="heading${accordion.id}" data-parent="#accordion${accordion.id}">
                                        <div class="accordion__panel">
                                            ${sectionAccordionContent}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </li>
                    `);
                });
            }).catch(function (ex) {
                console.log('Parsing failed', ex);
            });
    }

    renderSections() {
        let self = this;

        self.mapData.map(item => {
            // Create li element
            let li = document.createElement('li');
            li.setAttribute('class', 'sections__item');
            li.classList.add(item.backgroundColour);

            // Create a element
            let a = document.createElement('a');
            a.setAttribute('class', 'sections__item-link');
            a.setAttribute('data-map-sections-item', '');
            a.setAttribute('data-section', item.sectionHandle);
            a.setAttribute('href', '');
            a.textContent = item.sectionName;

            // Append a element to li element
            li.append(a);

            // Append li element to self.sectionsItemsContainer
            self.sectionsItemsContainer.append(li);

            // Add sections item to global self.sectionsItems var
            self.sectionsItems.push(a);
        });
    }

    events() {
        let self = this;

        // Loop through sections items
        self.sectionsItems.map(item => {
            // Handle sections item clicks
            item.addEventListener('click', evt => {
                evt.preventDefault();

                // Clear all markers from the map
                self.view.graphics.removeAll();

                // Get the handle of the section to switch to
                let sectionHandle = item.getAttribute('data-section');

                // Find the section from the mapData
                let newSection = self.mapData.find(section => section.sectionHandle == sectionHandle);

                // Load the section
                self.goToSection(newSection, item, true);
            });
        });
    }

    goToSection(section, sectionsItem, clicked) {
        let self = this;

        // Remove active class from all sections items
        self.sectionsItems.map(item => {
            item.classList.remove('is-active');
        });

        // Add active class to clicked button
        sectionsItem.classList.add('is-active');

        // Render the new set of markers
        self.renderMarkers(section);

        self.rendersectionBreakdownItems(section);

        self.selectedSectionsItemIndex = self.sectionsItems.findIndex((arrayItem) => arrayItem == sectionsItem);

        if (clicked == true) {
            if (section.sectionHandle != 'preferred-route') {
                self.center = [section.activities[0].centerPoint.lng, section.activities[0].centerPoint.lat];

                self.view.goTo({
                    center: self.center,
                    zoom: 13
                });
            } else {
                self.center = self.homeCenter;

                self.view.goTo({
                    center: self.center,
                    zoom: self.homeZoom
                });
            }
        }
    }

    renderMarkers(section) {
        let self = this;

        // Create empty array to store the markers
        let markers = [];

        // Get the markers from the section data
        if (section && section.activities) {
            section.activities.map(activity => {
                activity.markers.map(marker => {
                    marker.sectionName = section.sectionName;
                    marker.summary = activity.summary;
                    marker.icon = activity.icon_url;
                    markers.push(marker);
                });
            });
        }

        // Loop through the markers
        for (let i = 0; i < markers.length; i++) {
            // Get the marker data
            let markerData = markers[i];

            // Initiate media content items array
            let popupMediaContent = [];

            popupMediaContent.push({
                title: markerData.sectionName,
                caption: markerData.description
            });

            let viewpoints = '';

            if (markerData.viewpoints) {
                markerData.viewpoints.map(viewpoint => {
                    viewpoints = viewpoints + `<a href="${encodeURI(window.location.origin + viewpoint.link)}" target="_blank" style="display: inline-block; padding: 8px 12px; color: #fff; background-color: #1A523D; text-decoration: none; border: 1px solid #fff; font-weight: bold; margin-right: 10px;">${viewpoint.title}</a>`;
                });
            }

            let popupContent = `
                <p>${markerData.description}</p>
                ${viewpoints}
            `;

            // Build pop up
            const popupTemplate = {
                title: markerData.title,
                content: popupContent
            };

            const icon = self.getMarker(markerData.lng, markerData.lat, markerData.icon, popupTemplate, markerData.size);

            self.view.graphics.add(icon);
        }
    }

    rendersectionBreakdownItems(section) {
        let self = this;

        self.sectionBreakdownItems.innerHTML = '';

        // Add section breakdown items to the section breakdown dropdown
        if (section.activities.length == 1) {
            section.activities[0].markers.map(item => {
                self.sectionBreakdownItems.insertAdjacentHTML('beforeend', `
                    <li class="section-breakdown__item">
                        <span class="section-breakdown__item-icon">
                            <img alt="" src="${item.icon_url}">
                        </span>
                        <button class="[ section-breakdown__item-label ] [ stretched-link ]" type="button" data-section-breakdown-item-btn data-center-point-lat="${item.lat}" data-center-point-lng="${item.lng}">${item.title}</button>
                    </li>
                `);
            });
        } else {
            section.activities.map(item => {
                self.sectionBreakdownItems.insertAdjacentHTML('beforeend', `
                    <li class="section-breakdown__item">
                        <span class="section-breakdown__item-icon">
                            <img alt="" src="${item.icon_url}">
                        </span>
                        <button class="[ section-breakdown__item-label ] [ stretched-link ]" type="button" data-section-breakdown-item-btn data-center-point-lat="${item.centerPoint.lat}" data-center-point-lng="${item.centerPoint.lng}">${item.summary}</button>
                    </li>
                `);
            });
        }

        // Get all the section breakdown item buttons
        let sectionBreakdownItemButtons = self.sectionBreakdown.querySelectorAll('[data-section-breakdown-item-btn]');

        // Loop through the buttons and listen for clicks
        [...sectionBreakdownItemButtons].map(item => {
            if (item.getAttribute('data-center-point-lat') && item.getAttribute('data-center-point-lng')) {
                item.addEventListener('click', evt => {
                    let lat = Number(item.getAttribute('data-center-point-lat'));
                    let lng = Number(item.getAttribute('data-center-point-lng'));

                    self.center = [lng, lat];

                    self.view.goTo({
                        center: [self.center],
                        zoom: 13
                    });
                });
            };
        });
    }

    fetchMapLayers() {
        let self = this;

        fetch(`/data/map-layers.json`)
            .then(function (response) {
                return response.json();
            }).then(function (json) {
                self.mapLayers = json;

                self.addMapLayers(self.mapLayers);
            }).catch(function (ex) {
                console.log('Parsing failed', ex);
            });
    }

    addMapLayers(data) {
        let self = this;

        data.forEach((item) => {
            let content = null;

            switch (item.type) {
                case 'icon':
                    content = {
                        apiKey: self.apiKey,
                        url: item.url,
                        minScale: 144447,
                        maxScale: 0,
                        outFields: ['Label_New'],
                        popupEnabled: true,
                        popupTemplate: {
                            title: "{Label_New}"
                        },
                        renderer: {
                            type: "simple",
                            symbol: {
                                type: 'picture-marker',
                                url: item.icon_url,
                                width: '7px',
                                height: '7px'
                            }
                        }
                    }

                    break;

                case 'line':
                    content = {
                        apiKey: self.apiKey,
                        url: item.url,
                        minScale: 144447,
                        maxScale: 0,
                        renderer: {
                            type: "simple",
                            symbol: {
                                type: "simple-line",
                                color: item.color,
                                width: '2px'
                            }
                        }
                    }

                    break;

                case 'simple':
                    let red = '0';
                    let green = '0';
                    let blue = '0';
                    let color = `rgba(${red}, ${green}, ${blue}, 0)`;
                    let width = '0.5px';

                    if (item.color) {
                        red = parseInt(item.color.substring(1, 3), 16);
                        green = parseInt(item.color.substring(3, 5), 16);
                        blue = parseInt(item.color.substring(5, 7), 16);

                        color = `rgb(${red}, ${green}, ${blue})`;
                        width = '0px';

                        if (item.opacity) {
                            color = `rgba(${red}, ${green}, ${blue}, ${item.opacity})`;
                        }
                    }

                    content = {
                        apiKey: self.apiKey,
                        url: item.url,
                        minScale: null,
                        maxScale: 0,
                        renderer: {
                            type: "simple",
                            symbol: {
                                type: 'simple-fill',
                                color: color,
                                outline: {
                                    color: item.outline_color,
                                    style: 'solid',
                                    width: width
                                }
                            }
                        }
                    }

                    break;
            }

            let layer = new FeatureLayer(content);

            self.basemap.add(layer, item.arrangement);
            self.basemap.reorder(layer, item.arrangement);
        });
    }

    getAddressFormSubmission(event) {
        let self = this;

        event.preventDefault();

        const address = self.addressFormInput.value;

        if (address.trim() !== '') {
            self.getAddress(address);
        } else {
            console.error('Please provide a postcode');
        }
    }

    getAddress(address) {
        let self = this;

        self.view.graphics.remove(self.homeMarker);

        axios.get(`https://api.postcodes.io/postcodes/${address}`).then(res => {
            const coordinates = [res.data.result.latitude, res.data.result.longitude];
            const icon = self.getMarker(coordinates[1], coordinates[0], '/assets/images/map-icons/markers/home.svg');

            self.homeMarker = icon;

            self.view.graphics.add(icon);
            self.view.goTo({
                center: [coordinates[1], coordinates[0]],
                zoom: 13
            });
        }, er => {
            alert('Postcode not found. Please check your postcode and try again.');
        });
    }

    getMarker(lng, lat, icon, popup, size) {
        let self = this;

        let height = '53px';
        let width = '41px';
        let offsetY = 23;

        if (size) {
            height = '33px';
            width = '25px';
            offsetY = 14;
        }

        let symbol = new PictureMarkerSymbol({
            url: icon,
            width: width,
            height: height,
            yoffset: offsetY
        });

        let graphic = new Graphic({
            geometry: {
                type: "point",
                longitude: lng,
                latitude: lat
            },
            symbol: symbol
        });

        if (popup) {
            graphic.popupTemplate = popup;
        }

        return graphic;
    }
}

export default MapArcgis;
